import { useEffect, useRef, useState } from "react";

/**
 * Wraps children and reveals them on scroll into the viewport.
 * Lightweight CSS-driven animation triggered by IntersectionObserver.
 */
const HAS_IO =
  typeof window !== "undefined" && typeof window.IntersectionObserver !== "undefined";

export default function Reveal({
  children,
  direction = "up",          // "up" | "left" | "right" | "zoom"
  delay = 0,                  // ms
  threshold = 0.15,
  className = "",
  as: Tag = "div",
  ...rest
}) {
  const ref = useRef(null);
  // If no IO support, show immediately.
  const [visible, setVisible] = useState(!HAS_IO);

  useEffect(() => {
    if (!HAS_IO || visible) return;
    const node = ref.current;
    if (!node) return;

    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setVisible(true);
            obs.disconnect();
            break;
          }
        }
      },
      { threshold, rootMargin: "0px 0px -10% 0px" }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [threshold, visible]);

  const directionAttr = direction === "up" ? undefined : direction;

  return (
    <Tag
      ref={ref}
      data-reveal={visible ? "visible" : "hidden"}
      data-reveal-direction={directionAttr}
      className={className}
      style={{ transitionDelay: `${delay}ms`, ...rest.style }}
      {...rest}
    >
      {children}
    </Tag>
  );
}
